import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginForm from './components/LoginForm';
import MFAVerifyStep from './components/MFAVerifyStep';
import WaveBackground from './components/WaveBackground';

const LoginScreen = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signIn, verifyMFALogin, getAAL } = useAuth();
  const [step, setStep] = useState('login'); // 'login' | 'mfa'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState(null);

  // If already signed in, go straight to the dashboard (ITSM at root).
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (email, password) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: signInError } = await signIn(email, password);
      if (signInError) {
        setError(signInError?.message || 'Invalid email or password.');
        return;
      }
      // Check if MFA is required
      const { data: aalData } = await getAAL();
      if (
        aalData?.nextLevel === 'aal2' &&
        aalData?.currentLevel !== 'aal2'
      ) {
        // MFA required — find the TOTP factor
        const factors = data?.user?.factors || [];
        const totpFactor = factors?.find((f) => f?.factor_type === 'totp' && f?.status === 'verified');
        if (totpFactor) {
          setMfaFactorId(totpFactor?.id);
          setStep('mfa');
          return;
        }
      }
      // Sin MFA → al dashboard.
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerify = async (code) => {
    if (!mfaFactorId) return;
    setLoading(true);
    setError('');
    try {
      const { error: mfaError } = await verifyMFALogin(mfaFactorId, code);
      if (mfaError) {
        setError(mfaError?.message || 'Invalid verification code.');
        return;
      }
      // MFA OK → al dashboard.
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('login');
    setError('');
    setMfaFactorId(null);
  };

  if (authLoading) {
    return (
      <div
        className="dark flex items-center justify-center min-h-screen"
        style={{ backgroundColor: '#080d1e' }}
      >
        <WaveBackground />
        <div
          className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: '#60a5fa', borderTopColor: 'transparent', position: 'relative', zIndex: 1 }}
        />
      </div>
    );
  }

  return (
    <div
      className="dark min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: '#080d1e', position: 'relative' }}
    >
      <WaveBackground />
      {/* Card */}
      <div
        className="w-full max-w-[420px] rounded-2xl p-8"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.82)',
          border: '1px solid rgba(34, 211, 238, 0.22)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(52, 102, 244, 0.10)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Logo + Brand */}
        <div className="flex flex-col items-center gap-4 mb-8">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <img
              src="/assets/images/BlackLogo-1773090086774.png"
              alt="easyTEK ITSM Logo"
              style={{
                width: '40px',
                height: '40px',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </div>
          <div className="text-center">
            <h1
              className="text-xl font-bold tracking-tight"
              style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
            >
              easyTEK ITSM
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
              IT Service Management Platform
            </p>
          </div>
        </div>

        {/* Divider */}
        <div
          className="mb-6"
          style={{ height: '1px', backgroundColor: 'var(--color-border)' }}
        />

        {/* Step: Login or MFA */}
        {step === 'login' ? (
          <>
            <div className="mb-5">
              <h2
                className="text-base font-semibold"
                style={{ color: 'var(--color-foreground)' }}
              >
                Sign in to your account
              </h2>
              <p className="text-sm mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                Enter your credentials to continue
              </p>
            </div>
            <LoginForm
              onSubmit={handleLogin}
              loading={loading}
              error={error}
            />
          </>
        ) : (
          <MFAVerifyStep
            onVerify={handleMFAVerify}
            onBack={handleBack}
            loading={loading}
            error={error}
          />
        )}
      </div>
      {/* Footer */}
      <div className="mt-6 text-xs text-center" style={{ color: 'rgba(148, 163, 184, 0.6)', position: 'relative', zIndex: 1 }}>
        <p className="mb-1">Soporte easyTEK</p>
        <p>© {new Date()?.getFullYear()}. All rights reserved.</p>
      </div>
    </div>
  );
};

export default LoginScreen;
